import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import VisibilityIcon from "@mui/icons-material/Visibility"
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material"
import {
  Chapter,
  ChapterType,
  DataNode,
  DeviceEutGroup,
  DeviceTestEquipment,
  Examination,
  Project,
  Report,
  Status,
} from "graphql/types"
import { JSX, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useStoreActions, useStoreState } from "hooks/storeHooks"
import {
  getAdaptedStandard,
  getEmptyChapter,
  getEUT,
  getEUTAuxiliary,
  getEUTEntranceExamination,
  getPassFailCriteria,
  getTestEquipment,
  getTestLocationAndConditions,
  getTestOverviewData,
  getTestParametersAndCategory,
  getTestResults,
  getTestSetup,
  getWitnesses,
} from "store/buildReport"
import { reportEUTChapterTemplate, reportTestChapterTemplate } from "store/reportChaptersTemplate"
import { isDeviceTe, isEutGroup } from "store/typeGuards"
import { allExaminations, allExaminationsUnique } from "utils/allExaminations"
import { ensureAppendixA, updateReportChapterNumbering } from "utils/updateReportChapterNumbering"
import { updateReportNumbering } from "utils/updateReportNumbering"
import { v4 } from "uuid"
import { HierarchicalChapterList } from "./HierarchicalChapterList"

type ReportIndexBoxProps = {
  project: Project
  report: Report
  // eslint-disable-next-line no-unused-vars
  onReportChange: (updatedReport: Report) => void
}

export const ReportIndexBox = (props: ReportIndexBoxProps): JSX.Element => {
  const { project, report, onReportChange } = props
  const [localReport, setLocalReport] = useState<Report>(report)
  const [searchParams, setSearchParams] = useSearchParams()
  const { employees, textBlocks } = useStoreState((state) => state)
  const { fetchTextBlocks } = useStoreActions((state) => state)
  const [openAddAppendixDialog, setOpenAddAppendixDialog] = useState(false)
  const [appendixName, setAppendixName] = useState("")

  useEffect(() => {
    setLocalReport(report)
  }, [report])

  // Load text blocks on component mount
  useEffect(() => {
    fetchTextBlocks({ fresh: false })
  }, [fetchTextBlocks])

  // Ensure Appendix A is always present
  useEffect(() => {
    const reportWithAppendixA = ensureAppendixA(localReport, textBlocks)
    if (reportWithAppendixA !== localReport) {
      const updatedReport = updateReportChapterNumbering(reportWithAppendixA, textBlocks)
      setLocalReport(updatedReport)
      onReportChange(updatedReport)
    }
  }, [localReport, onReportChange, textBlocks])

  const handleExaminationAdd = (examination: Examination | null) => {
    if (examination) {
      // 2 Test Overview and Results
      const testOverviewChap = localReport.chapter.find(
        (item) => item.type === ChapterType.OVERVIEW
      )
      const newTestOverviewChapter: Chapter = {
        id: testOverviewChap ? testOverviewChap.id : v4(),
        type: ChapterType.OVERVIEW,
        name: "Test Overview and Results",
        chapterNumber: "2",
        subChapter: [],
        open: false,
        data: getTestOverviewData(
          examination,
          testOverviewChap?.data || [],
          report,
          project.devices.filter((item) => isEutGroup(item)) as DeviceEutGroup[],
          [...localReport.examinations, examination],
          employees
        ),
      }

      // 3 Equipment under Test
      const newEUTChapter = {
        ...reportEUTChapterTemplate,
        subChapter: reportEUTChapterTemplate.subChapter?.map((chap) => {
          switch (chap.type) {
            // 3.1
            case ChapterType.EUT_EUT:
              const existingEUTChapter = localReport.chapter.find(
                (chap) => chap.type === ChapterType.EUT
              )
              const existingEUTData =
                existingEUTChapter?.subChapter?.find((sub) => sub.type === ChapterType.EUT_EUT)
                  ?.data || []

              return {
                ...chap,
                data: getEUT(
                  [...localReport.examinations, examination],
                  project.devices.filter((item) => isEutGroup(item)) as DeviceEutGroup[],
                  existingEUTData // Preserve existing manual data
                ),
              }
            // 3.2
            case ChapterType.EUT_AUX:
              const existingEUTChapterForAux = localReport.chapter.find(
                (chap) => chap.type === ChapterType.EUT
              )
              const existingAuxData =
                existingEUTChapterForAux?.subChapter?.find(
                  (sub) => sub.type === ChapterType.EUT_AUX
                )?.data || []

              return {
                ...chap,
                data: getEUTAuxiliary(
                  [...localReport.examinations, examination], // All examinations
                  project.devices,
                  existingAuxData // Preserve existing manual data
                ),
              }
            // 3.3
            case ChapterType.EUT_ENTRANCE_EXAMEN:
              const existingEUTChapterForEntrance = localReport.chapter.find(
                (chap) => chap.type === ChapterType.EUT
              )
              const existingEntranceData =
                existingEUTChapterForEntrance?.subChapter?.find(
                  (sub) => sub.type === ChapterType.EUT_ENTRANCE_EXAMEN
                )?.data || []

              return {
                ...chap,
                data: getEUTEntranceExamination(
                  existingEntranceData // Preserve existing manual data
                ),
              }
            // 3.4
            case ChapterType.EUT_CONNECTIONS:
              return { ...chap, data: getEmptyChapter() }
            // 3.5
            case ChapterType.EUT_FUNCTIONAL_CHECKS:
              return { ...chap, data: getEmptyChapter() }
            // 3.6
            // case ChapterType.EUT_GENERAL_PASS_FAIL_CRITERIA:
            //   return { ...chap, data: getEmptyChapter() }
            default:
              return { ...chap, data: getEmptyChapter() }
          }
        }),
      }

      // Build the complete chapter structure in the correct order first
      const indexChapter = localReport.chapter.find((chap) => chap.type === ChapterType.INDEX)

      const existingTestChapters = localReport.chapter.filter(
        (chap) => chap.examinationId && chap.examinationId !== examination.id
      )

      // Calculate the correct chapter number for this examination
      const testChapterNumber = 4 + existingTestChapters.length

      // 4 Test Chapters
      const newSubChapter = reportTestChapterTemplate.subChapter?.map((chap, index) => {
        switch (chap.type) {
          // 4.1
          case ChapterType.TEST_SPECIFICATIONS:
            const newSpecSubChapter = chap.subChapter?.map((sub, subIndex) => {
              // For NEW test chapters, don't use existingData - start fresh
              switch (sub.type) {
                // 4.1.1 Adapted Standard - Examination-specific
                case ChapterType.TEST_SPECIFICATIONS_ADAPTED_STANDARD:
                  return {
                    ...sub,
                    id: v4(), // Generate new ID for each examination
                    chapterNumber: `${testChapterNumber}.${index + 1}.${subIndex + 1}`, // Set correct sub-subchapter number
                    data: getAdaptedStandard(examination), // Examination-specific data
                  }
                // 4.1.2 Test Qualification Plan - Project-wide but examination-specific instance
                case ChapterType.TEST_SPECIFICATIONS_QTP:
                  return {
                    ...sub,
                    id: v4(), // Generate new ID for each examination
                    chapterNumber: `${testChapterNumber}.${index + 1}.${subIndex + 1}`, // Set correct sub-subchapter number
                    data: getEmptyChapter(),
                  }
                // 4.1.3 Test Parameter / Category - Examination-specific
                case ChapterType.TEST_SPECIFICATIONS_PARAMETER_AND_CATEGORY:
                  return {
                    ...sub,
                    id: v4(), // Generate new ID for each examination
                    chapterNumber: `${testChapterNumber}.${index + 1}.${subIndex + 1}`, // Set correct sub-subchapter number
                    data: getTestParametersAndCategory(examination), // Examination-specific data
                  }
                // 4.1.4 Deviations - Examination-specific
                case ChapterType.TEST_SPECIFICATIONS_DEVITATIONS:
                  return {
                    ...sub,
                    id: v4(), // Generate new ID for each examination
                    chapterNumber: `${testChapterNumber}.${index + 1}.${subIndex + 1}`, // Set correct sub-subchapter number
                    data: getEmptyChapter(),
                  }
                // 4.1.5 Remarks - Examination-specific
                case ChapterType.TEST_SPECIFICATIONS_REMARKS:
                  return {
                    ...sub,
                    id: v4(), // Generate new ID for each examination
                    chapterNumber: `${testChapterNumber}.${index + 1}.${subIndex + 1}`, // Set correct sub-subchapter number
                    data: getEmptyChapter(),
                  }
                // 4.1.6 Pass/Fail Criteria - Examination-specific
                case ChapterType.TEST_SPECIFICATIONS_PASS_FAIL_CRITERIA:
                  return {
                    ...sub,
                    id: v4(), // Generate new ID for each examination
                    chapterNumber: `${testChapterNumber}.${index + 1}.${subIndex + 1}`, // Set correct sub-subchapter number
                    data: getPassFailCriteria(examination),
                  }
                default:
                  return {
                    ...sub,
                    id: v4(), // Generate new ID for each examination
                    chapterNumber: `${testChapterNumber}.${index + 1}.${subIndex + 1}`, // Set correct sub-subchapter number
                  }
              }
            })
            return {
              ...chap,
              id: v4(), // Generate new ID for each examination
              chapterNumber: `${testChapterNumber}.${index + 1}`, // Set correct subchapter number
              subChapter: newSpecSubChapter,
            }
          // 4.2 Location and Conditions - Examination-specific
          case ChapterType.TEST_LOCATION_AND_CONDITIONS:
            return {
              ...chap,
              id: v4(), // Generate new ID for each examination
              chapterNumber: `${testChapterNumber}.${index + 1}`, // Set correct subchapter number
              data: getTestLocationAndConditions(examination, report), // Examination-specific data
            }
          // 4.3 Test Equipment - Examination-specific
          case ChapterType.TEST_EQUIPMENT:
            return {
              ...chap,
              id: v4(), // Generate new ID for each examination
              chapterNumber: `${testChapterNumber}.${index + 1}`, // Set correct subchapter number
              data: getTestEquipment(
                examination,
                project.devices.filter((item) => isDeviceTe(item)) as DeviceTestEquipment[]
              ), // Examination-specific data
            }
          // 4.4 External Witnesses - Examination-specific
          case ChapterType.TEST_EXTERNAL_WITNESS:
            return {
              ...chap,
              id: v4(), // Generate new ID for each examination
              chapterNumber: `${testChapterNumber}.${index + 1}`, // Set correct subchapter number
              data: getWitnesses(examination), // Examination-specific data
            }
          // 4.5 Test Setup - Examination-specific
          case ChapterType.TEST_SETUP:
            return {
              ...chap,
              id: v4(), // Generate new ID for each examination
              chapterNumber: `${testChapterNumber}.${index + 1}`, // Set correct subchapter number
              data: getTestSetup(examination), // Examination-specific data
            }
          // 4.6 Results - Examination-specific
          case ChapterType.TEST_RESULTS:
            return {
              ...chap,
              id: v4(), // Generate new ID for each examination
              chapterNumber: `${testChapterNumber}.${index + 1}`, // Set correct subchapter number
              data: getTestResults(examination, []), // Examination-specific data
            }

          default:
            return {
              ...chap,
              id: v4(), // Generate new ID for each examination
              chapterNumber: `${testChapterNumber}.${index + 1}`, // Set correct subchapter number
            }
        }
      })

      const newTestChapter: Chapter = {
        ...reportTestChapterTemplate,
        name: examination.examinationConfig.name,
        examinationId: examination.id,
        id: v4(),
        chapterNumber: String(testChapterNumber), // Each examination gets its own chapter number
        subChapter: newSubChapter,
      }

      // Get existing appendix chapters (both APPENDIX_A and user-defined APPENDIX)
      const existingAppendixChapters = localReport.chapter.filter(
        (chapter) =>
          chapter.type === ChapterType.APPENDIX || chapter.type === ChapterType.APPENDIX_A
      )

      // Create new chapters array with proper ordering:
      // Index(0), Overview(1), EUT(2), Test Chapters(3+), Appendix Chapters
      const newChapters: Chapter[] = [
        ...(indexChapter ? [indexChapter] : []), // Position 0: Index
        newTestOverviewChapter, // Position 1: Test Overview
        newEUTChapter, // Position 2: Equipment Under Test (always use updated version)
        ...existingTestChapters, // Position 4+: Existing Test Chapters
        newTestChapter, // Position 3: New Test Chapter
        ...existingAppendixChapters, // Append all appendix chapters
      ]

      const updatedReport = updateReportNumbering({
        ...localReport,
        chapter: newChapters,
        examinations: [...localReport.examinations, examination],
      })

      const newReport = updateReportChapterNumbering(updatedReport, textBlocks)

      setLocalReport(newReport)
      onReportChange(newReport)
      const newSearchParams = new URLSearchParams(searchParams.toString())
      newSearchParams.set("examinationID", examination.id)
      setSearchParams(newSearchParams)
    }
  }

  const handleExaminationRemove = (examinationID: string) => {
    // Remove Examination from report examination list and chapters

    const exams = allExaminations(project)
    const newExaminations: Examination[] = localReport.examinations
      .filter((e) => e.id !== examinationID)
      .map((e) => exams.find((exam) => exam.id === e.id))
      .filter((e) => e !== undefined) as Examination[]

    let newChapter = localReport.chapter.filter((e) => e.examinationId !== examinationID)

    // If no examinations remain, also remove the EUT chapter (Chapter 3)
    if (newExaminations.length === 0) {
      newChapter = newChapter.filter((chapter) => chapter.type !== ChapterType.EUT)
    }

    // Remove Examination data from Test Overview Chapter
    const testOverviewChap = localReport.chapter.find((item) => item.type === ChapterType.OVERVIEW)

    let newTestOverviewData: DataNode[] = []

    newExaminations.forEach((item) => {
      if (newTestOverviewData.length > 0) {
        const oldData = newTestOverviewData
        newTestOverviewData = getTestOverviewData(
          item,
          oldData,
          report,
          project.devices.filter((device) => isEutGroup(device)) as DeviceEutGroup[],
          newExaminations,
          employees
        )
      } else {
        newTestOverviewData = getTestOverviewData(
          item,
          [],
          report,
          project.devices.filter((device) => isEutGroup(device)) as DeviceEutGroup[],
          newExaminations,
          employees
        )
      }
    })

    const newTestOverviewChapter: Chapter = {
      id: testOverviewChap ? testOverviewChap.id : v4(),
      type: ChapterType.OVERVIEW,
      name: "Test Overview and Results",
      chapterNumber: "2",
      subChapter: [],
      open: false,
      data: newTestOverviewData,
    }

    // Find the index of the overview chapter and update it
    const overviewIndex = newChapter.findIndex((chap) => chap.type === ChapterType.OVERVIEW)
    if (overviewIndex !== -1) {
      newChapter[overviewIndex] = newTestOverviewChapter
    }

    // Update EUT chapter with remaining examinations
    if (newExaminations.length > 0) {
      const eutChapterIndex = newChapter.findIndex((chap) => chap.type === ChapterType.EUT)
      if (eutChapterIndex !== -1) {
        const existingEUTChapter = newChapter[eutChapterIndex]

        const updatedEUTChapter = {
          ...existingEUTChapter,
          subChapter: existingEUTChapter.subChapter?.map((chap) => {
            switch (chap.type) {
              case ChapterType.EUT_EUT:
                const existingEUTData =
                  existingEUTChapter?.subChapter?.find((sub) => sub.type === ChapterType.EUT_EUT)
                    ?.data || []
                return {
                  ...chap,
                  data: getEUT(
                    newExaminations,
                    project.devices.filter((item) => isEutGroup(item)) as DeviceEutGroup[],
                    existingEUTData
                  ),
                }
              case ChapterType.EUT_AUX:
                const existingAuxData =
                  existingEUTChapter?.subChapter?.find((sub) => sub.type === ChapterType.EUT_AUX)
                    ?.data || []
                return {
                  ...chap,
                  data: getEUTAuxiliary(
                    newExaminations, // Only remaining examinations
                    project.devices,
                    existingAuxData // Preserve existing manual data
                  ),
                }
              case ChapterType.EUT_ENTRANCE_EXAMEN:
                const existingEntranceData =
                  existingEUTChapter?.subChapter?.find(
                    (sub) => sub.type === ChapterType.EUT_ENTRANCE_EXAMEN
                  )?.data || []
                return {
                  ...chap,
                  data: getEUTEntranceExamination(existingEntranceData),
                }
              default:
                return chap
            }
          }),
        }
        newChapter[eutChapterIndex] = updatedEUTChapter
      }
    }

    // Update the with the new chapter
    const updatedReport = updateReportNumbering({
      ...localReport,

      chapter: newChapter,
      examinations: newExaminations,
    })

    const newReport = updateReportChapterNumbering(updatedReport, textBlocks)

    setLocalReport(newReport)
    onReportChange(newReport)
    if (searchParams.get("examinationID")) {
      searchParams.delete("examinationID")
      setSearchParams(searchParams)
    }
  }

  const updateChapter = (newChapter: Chapter) => {
    const updateChapterRecursively = (chapters: Chapter[]): Chapter[] => {
      return chapters.map((item) => {
        if (item.id === newChapter.id) {
          return newChapter
        }
        if (item.subChapter) {
          return {
            ...item,
            subChapter: updateChapterRecursively(item.subChapter),
          }
        }
        return item
      })
    }
    const newReport = {
      ...report,
      chapter: updateChapterRecursively(report.chapter),
    }
    onReportChange(newReport)
  }

  const handleAddAppendix = () => {
    if (!appendixName.trim()) return

    const newAppendix: Chapter = {
      id: v4(),
      type: ChapterType.APPENDIX,
      name: appendixName.trim(),
      chapterNumber: "", // Will be set by updateReportChapterNumbering
      subChapter: [],
      open: false,
      data: [],
    }

    // Add the new appendix to the chapters array
    const newChapters = [...localReport.chapter, newAppendix]

    // Update the report with proper numbering
    const updatedReport = updateReportNumbering({
      ...localReport,
      chapter: newChapters,
    })

    const newReport = updateReportChapterNumbering(updatedReport, textBlocks)

    setLocalReport(newReport)
    onReportChange(newReport)
    setOpenAddAppendixDialog(false)
    setAppendixName("")
  }

  const handleOpenAddAppendixDialog = () => {
    setOpenAddAppendixDialog(true)
  }

  const handleCloseAddAppendixDialog = () => {
    setOpenAddAppendixDialog(false)
    setAppendixName("")
  }

  return (
    <>
      {localReport !== null && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <Typography variant="h5">Report Kapitel</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAddAppendixDialog}
                sx={{ ml: 2 }}
              >
                Anhang hinzufügen
              </Button>
            </Box>
            <HierarchicalChapterList chapters={report.chapter} updateChapter={updateChapter} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h5">Prüfungen</Typography>

            <Autocomplete
              id="examination-select"
              fullWidth
              size="small"
              options={(() => {
                const allExams = allExaminationsUnique(project)
                // Temporarily show ALL examinations for debugging
                const showAllForDebug = true
                const filteredExams = allExams.filter(
                  (examination) =>
                    !localReport.examinations.some(
                      (reportExamination) => reportExamination.id === examination.id
                    ) &&
                    (showAllForDebug || examination.status === Status.DONE)
                )

                // Debug logging

                return filteredExams
              })()}
              autoHighlight
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props
                return (
                  <Box key={key} component="li" {...optionProps}>
                    {option.name}
                  </Box>
                )
              }}
              renderInput={(params) => <TextField {...params} label="Prüfung auswählen" />}
              onChange={(_, value) => handleExaminationAdd(value)}
            />

            {localReport.examinations.map((examination) => (
              <Card key={examination.id}>
                <CardHeader
                  avatar={<Avatar alt={examination.name} variant="square" />}
                  title={examination.name}
                  action={
                    <>
                      <IconButton onClick={() => handleExaminationRemove(examination.id)}>
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          const newSearchParams = new URLSearchParams(searchParams.toString())
                          newSearchParams.set("examinationID", examination.id)
                          setSearchParams(newSearchParams)
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </>
                  }
                />
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Add Appendix Dialog */}
      <Dialog open={openAddAppendixDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Anhang hinzufügen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Anhang Name"
            fullWidth
            variant="outlined"
            value={appendixName}
            onChange={(e) => setAppendixName(e.target.value)}
            placeholder="z.B. Applied Conformity Assessment Rule"
            helperText="Der Anhang wird automatisch als Appendix A, B, C... nummeriert"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddAppendixDialog}>Abbrechen</Button>
          <Button onClick={handleAddAppendix} variant="contained" disabled={!appendixName.trim()}>
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
